// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment, useState } from 'react';

import { Accordion, Card, Divider, Flex, Space, Stack, Text } from '@mantine/core';

import { StatusPoint } from 'components/Mantine/StatusPoint';

import { AddEditStatusPopover } from './AddEditStatusPopover';
import { DeleteStatusModal } from './DeleteStatusModal';
import { StatusRow } from './StatusRow';
import type { CustomStatus, CustomStatusEntityName, EntityState, StateIdFor } from './types';
import { useCustomStatuses } from './useCustomStatuses';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  state: EntityState<TEntity>;
  defaultOpen?: boolean;
};

/**
 * One state's accordion section. Shows the canonical state header with its dot color,
 * a child row per custom status with edit/delete, and a popover-triggered "add status" form
 * on the header's plus icon.
 */
export const StateAccordionItem = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  state,
  defaultOpen,
}: Props<TEntity>) => {
  const { stateColorMap } = useCustomStatuses(customStatusTypeName);
  const { color, label } = stateColorMap[state.name];

  const [opened, setOpened] = useState<StateIdFor<TEntity> | null>(defaultOpen ? state.stateId : null);
  const [confirmDelete, setConfirmDelete] = useState<CustomStatus<TEntity> | null>(null);

  return (
    <Card withBorder radius={4} p={0} style={{ overflow: 'hidden' }}>
      <Accordion
        value={opened}
        onChange={(value) => setOpened(value as StateIdFor<TEntity> | null)}
        styles={{
          content: { padding: 0, minHeight: 33 },
          item: { borderBottom: 'none' },
        }}
      >
        <Accordion.Item key={state.stateId} value={state.stateId}>
          <Accordion.Control icon={<StatusPoint color={color} tooltip={state.name} />} bg="gray.1">
            <Flex align="center" justify="space-between" mr="md">
              <Text fw="bold" fz="sm">
                {label}
              </Text>

              <AddEditStatusPopover
                customStatusTypeName={customStatusTypeName}
                stateId={state.stateId}
                onOpen={() => setOpened(state.stateId)}
              />
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
            {state.statuses.length === 0 && (
              <Text c="dimmed" fz="sm" m="xs" mx="md">
                No statuses.
              </Text>
            )}
            <Stack gap={0}>
              {state.statuses.map((status) => (
                <Fragment key={status.statusId}>
                  <Divider w="100%" />
                  <StatusRow
                    customStatusTypeName={customStatusTypeName}
                    status={status}
                    onDeleteStatus={setConfirmDelete}
                  />
                </Fragment>
              ))}
              <Space />
            </Stack>
            <DeleteStatusModal
              customStatusTypeName={customStatusTypeName}
              status={confirmDelete}
              onClose={() => setConfirmDelete(null)}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
};
